import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPage = () => {
  const { language } = useLanguage();

  // Get canonical URL
  const getCanonicalUrl = () => {
    if (language === 'en') return 'https://villa.ooo/privacy';
    return `https://villa.ooo/${language}/privacy`;
  };

  // Supported languages for hreflang
  const hreflangLanguages = ['en', 'de', 'pl', 'sv', 'cs'];

  // Get og:locale
  const getOgLocale = () => {
    const locales = { en: 'en_US', de: 'de_DE', pl: 'pl_PL', sv: 'sv_SE', cs: 'cs_CZ' };
    return locales[language] || 'en_US';
  };

  // Localized content
  const content = {
    en: {
      title: 'Privacy Policy',
      metaDesc: 'Privacy Policy for Villa.ooo - how we collect, use, and protect your personal data.',
      lastUpdated: 'Last Updated: April 2026',
      intro: 'At Villa.ooo, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or use our services.',
      sections: [
        {
          title: '1. Information We Collect',
          content: 'We may collect personal information that you voluntarily provide when you:\n• Submit a contact form or inquiry\n• Request information about our villas\n• Subscribe to our newsletter\n• Communicate with us via email or WhatsApp\n\nThis may include your name, email address, phone number, and any message content you provide.\n\nWe also automatically collect certain technical information when you visit our website:\n• IP address (used to determine approximate geographic location: country and city)\n• Browser type and version\n• Operating system\n• Device type (desktop, mobile, tablet)\n• Screen resolution\n• Timezone\n• Referrer URL (website you came from)\n• Pages visited on our site\n• UTM parameters (for marketing campaign tracking)'
        },
        {
          title: '2. How We Use Your Information',
          content: 'We use the information we collect to:\n• Respond to your inquiries about our properties\n• Provide you with information about villas that match your preferences\n• Send you updates about new listings (if you opted in)\n• Improve our website and services\n• Analyze website traffic and user behavior\n• Measure effectiveness of marketing campaigns\n• Comply with legal obligations\n\nGeographic data (country/city) helps us understand where our visitors come from and provide better localized service.'
        },
        {
          title: '3. Data Protection',
          content: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Your data is stored securely and accessed only by authorized personnel.\n\nIP addresses are stored temporarily and used only for geographic identification. We do not sell or share your personal data with third parties for marketing purposes.'
        },
        {
          title: '4. Third-Party Services',
          content: 'Our website may use third-party services such as:\n• Google Analytics and Google Tag Manager for website traffic analysis\n• Google Ads for remarketing and conversion tracking\n• Cloudinary for image hosting\n• WhatsApp for communication\n• ipapi.co for IP-based geolocation (determines country/city from IP address)\n• Google reCAPTCHA for spam protection\n\nThese services have their own privacy policies, and we encourage you to review them.'
        },
        {
          title: '5. Cookies and Tracking',
          content: 'We use cookies and similar technologies to:\n• Remember your language preferences\n• Analyze website usage\n• Track marketing campaign effectiveness (UTM parameters)\n• Provide personalized content\n\nYou can control cookie settings through your browser preferences.'
        },
        {
          title: '6. Your Rights',
          content: 'Under GDPR and applicable data protection laws, you have the right to:\n• Access your personal data\n• Request correction of inaccurate data\n• Request deletion of your data\n• Object to processing of your data\n• Request data portability\n• Withdraw consent at any time\n\nTo exercise these rights, please contact us at sales@villa.ooo.'
        },
        {
          title: '7. Data Retention',
          content: 'We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by law. Contact form submissions are retained for up to 3 years for business purposes.'
        },
        {
          title: '8. Contact Us',
          content: 'If you have any questions about this Privacy Policy or our data practices, please contact us:\n\nEmail: sales@villa.ooo\nWhatsApp: +357 95 989 569\n\nData Controller: Ingemar Ernst d.o.o.'
        }
      ]
    },
    de: {
      title: 'Datenschutzerklärung',
      metaDesc: 'Datenschutzerklärung für Villa.ooo - wie wir Ihre persönlichen Daten erheben, verwenden und schützen.',
      lastUpdated: 'Letzte Aktualisierung: April 2026',
      intro: 'Bei Villa.ooo verpflichten wir uns zum Schutz Ihrer Privatsphäre. Diese Datenschutzerklärung erläutert, wie wir Ihre persönlichen Daten erheben, verwenden und schützen, wenn Sie unsere Website besuchen oder unsere Dienste nutzen.',
      sections: [
        {
          title: '1. Welche Daten wir erheben',
          content: 'Wir können persönliche Daten erheben, die Sie freiwillig angeben, wenn Sie:\n• Ein Kontaktformular oder eine Anfrage absenden\n• Informationen über unsere Villen anfordern\n• Unseren Newsletter abonnieren\n• Mit uns per E-Mail oder WhatsApp kommunizieren\n\nDies kann Ihren Namen, Ihre E-Mail-Adresse, Telefonnummer und Nachrichteninhalte umfassen.\n\nWir erfassen auch automatisch technische Informationen:\n• IP-Adresse (zur Bestimmung des ungefähren Standorts: Land und Stadt)\n• Browsertyp und -version\n• Betriebssystem\n• Gerätetyp (Desktop, Mobil, Tablet)\n• Bildschirmauflösung\n• Zeitzone\n• Referrer-URL\n• UTM-Parameter (für Marketing-Tracking)'
        },
        {
          title: '2. Wie wir Ihre Daten verwenden',
          content: 'Wir verwenden die erhobenen Daten, um:\n• Auf Ihre Anfragen zu unseren Immobilien zu antworten\n• Ihnen Informationen über passende Villen zu senden\n• Ihnen Updates über neue Angebote zu senden (wenn Sie zugestimmt haben)\n• Unsere Website und Dienste zu verbessern\n• Website-Traffic und Nutzerverhalten zu analysieren\n• Effektivität von Marketingkampagnen zu messen\n• Gesetzlichen Verpflichtungen nachzukommen'
        },
        {
          title: '3. Datenschutz',
          content: 'Wir setzen geeignete technische und organisatorische Maßnahmen um, um Ihre persönlichen Daten vor unbefugtem Zugriff, Änderung, Offenlegung oder Zerstörung zu schützen. IP-Adressen werden nur temporär gespeichert und nur zur geografischen Identifizierung verwendet.'
        },
        {
          title: '4. Drittanbieterdienste',
          content: 'Unsere Website kann Drittanbieterdienste nutzen wie:\n• Google Analytics und Google Tag Manager für Website-Analyse\n• Google Ads für Remarketing\n• Cloudinary für Bild-Hosting\n• WhatsApp für Kommunikation\n• ipapi.co für IP-basierte Geolokalisierung\n• Google reCAPTCHA für Spam-Schutz'
        },
        {
          title: '5. Cookies und Tracking',
          content: 'Wir verwenden Cookies und ähnliche Technologien für:\n• Speichern Ihrer Spracheinstellungen\n• Analyse der Website-Nutzung\n• Marketing-Kampagnen-Tracking (UTM-Parameter)\n• Personalisierte Inhalte'
        },
        {
          title: '6. Ihre Rechte',
          content: 'Nach der DSGVO haben Sie das Recht auf:\n• Zugang zu Ihren persönlichen Daten\n• Berichtigung unrichtiger Daten\n• Löschung Ihrer Daten\n• Widerspruch gegen die Verarbeitung\n• Datenübertragbarkeit\n• Widerruf der Einwilligung\n\nKontaktieren Sie uns unter sales@villa.ooo.'
        },
        {
          title: '7. Datenspeicherung',
          content: 'Wir speichern Ihre Daten nur so lange wie nötig. Kontaktformular-Anfragen werden bis zu 3 Jahre aufbewahrt.'
        },
        {
          title: '8. Kontakt',
          content: 'Bei Fragen kontaktieren Sie uns:\n\nE-Mail: sales@villa.ooo\nWhatsApp: +357 95 989 569\n\nVerantwortlicher: Ingemar Ernst d.o.o.'
        }
      ]
    },
    pl: {
      title: 'Polityka Prywatności',
      metaDesc: 'Polityka Prywatności Villa.ooo - jak zbieramy, wykorzystujemy i chronimy Twoje dane osobowe.',
      lastUpdated: 'Ostatnia aktualizacja: kwiecień 2026',
      intro: 'W Villa.ooo zobowiązujemy się do ochrony Twojej prywatności. Niniejsza Polityka Prywatności wyjaśnia, jak zbieramy, wykorzystujemy i chronimy Twoje dane osobowe podczas odwiedzania naszej strony lub korzystania z naszych usług.',
      sections: [
        {
          title: '1. Jakie dane zbieramy',
          content: 'Możemy zbierać dane osobowe, które dobrowolnie podajesz, gdy:\n• Wysyłasz formularz kontaktowy lub zapytanie\n• Prosisz o informacje o naszych willach\n• Zapisujesz się do newslettera\n• Komunikujesz się z nami przez e-mail lub WhatsApp\n\nMoże to obejmować Twoje imię, adres e-mail, numer telefonu i treść wiadomości.\n\nAutomatycznie zbieramy również dane techniczne:\n• Adres IP (do określenia przybliżonej lokalizacji: kraju i miasta)\n• Typ i wersja przeglądarki\n• System operacyjny\n• Typ urządzenia (komputer, telefon, tablet)\n• Rozdzielczość ekranu\n• Strefa czasowa\n• URL odsyłający\n• Parametry UTM (do śledzenia kampanii marketingowych)'
        },
        {
          title: '2. Jak wykorzystujemy Twoje dane',
          content: 'Wykorzystujemy zebrane dane, aby:\n• Odpowiadać na Twoje zapytania o nieruchomości\n• Wysyłać informacje o odpowiednich willach\n• Wysyłać aktualizacje o nowych ofertach (jeśli wyraziłeś zgodę)\n• Ulepszać naszą stronę i usługi\n• Analizować ruch na stronie\n• Mierzyć skuteczność kampanii marketingowych'
        },
        {
          title: '3. Ochrona danych',
          content: 'Wdrażamy odpowiednie środki techniczne i organizacyjne w celu ochrony Twoich danych. Adresy IP są przechowywane tymczasowo i używane tylko do identyfikacji geograficznej.'
        },
        {
          title: '4. Usługi zewnętrzne',
          content: 'Nasza strona może korzystać z usług zewnętrznych:\n• Google Analytics i Google Tag Manager\n• Google Ads do remarketingu\n• Cloudinary do hostingu obrazów\n• WhatsApp do komunikacji\n• ipapi.co do geolokalizacji IP\n• Google reCAPTCHA do ochrony przed spamem'
        },
        {
          title: '5. Pliki cookie i śledzenie',
          content: 'Używamy plików cookie do:\n• Zapamiętywania preferencji językowych\n• Analizy użytkowania strony\n• Śledzenia kampanii marketingowych (parametry UTM)\n• Personalizacji treści'
        },
        {
          title: '6. Twoje prawa',
          content: 'Zgodnie z RODO masz prawo do:\n• Dostępu do swoich danych\n• Sprostowania nieprawidłowych danych\n• Usunięcia danych\n• Sprzeciwu wobec przetwarzania\n• Przenoszenia danych\n• Wycofania zgody\n\nSkontaktuj się z nami: sales@villa.ooo'
        },
        {
          title: '7. Przechowywanie danych',
          content: 'Przechowujemy dane tylko tak długo, jak to konieczne. Formularze kontaktowe są przechowywane do 3 lat.'
        },
        {
          title: '8. Kontakt',
          content: 'W razie pytań skontaktuj się z nami:\n\nE-mail: sales@villa.ooo\nWhatsApp: +357 95 989 569\n\nAdministrator danych: Ingemar Ernst d.o.o.'
        }
      ]
    },
    sv: {
      title: 'Integritetspolicy',
      metaDesc: 'Integritetspolicy för Villa.ooo - hur vi samlar in, använder och skyddar dina personuppgifter.',
      lastUpdated: 'Senast uppdaterad: april 2026',
      intro: 'På Villa.ooo är vi engagerade i att skydda din integritet. Denna integritetspolicy förklarar hur vi samlar in, använder och skyddar dina personuppgifter när du besöker vår webbplats eller använder våra tjänster.',
      sections: [
        {
          title: '1. Information vi samlar in',
          content: 'Vi kan samla in personuppgifter som du frivilligt tillhandahåller när du:\n• Skickar in ett kontaktformulär eller förfrågan\n• Begär information om våra villor\n• Prenumererar på vårt nyhetsbrev\n• Kommunicerar med oss via e-post eller WhatsApp\n\nDetta kan inkludera ditt namn, e-postadress, telefonnummer och meddelandeinnehåll.\n\nVi samlar också automatiskt in teknisk information:\n• IP-adress (för att fastställa ungefärlig plats: land och stad)\n• Webbläsartyp och version\n• Operativsystem\n• Enhetstyp (dator, mobil, surfplatta)\n• Skärmupplösning\n• Tidszon\n• Referens-URL\n• UTM-parametrar (för marknadsföringsspårning)'
        },
        {
          title: '2. Hur vi använder din information',
          content: 'Vi använder informationen vi samlar in för att:\n• Svara på dina frågor om våra fastigheter\n• Ge dig information om villor som matchar dina preferenser\n• Skicka uppdateringar om nya listor (om du valt det)\n• Förbättra vår webbplats och tjänster\n• Analysera webbplatstrafik och användarbeteende\n• Mäta effektiviteten av marknadsföringskampanjer'
        },
        {
          title: '3. Dataskydd',
          content: 'Vi implementerar lämpliga tekniska och organisatoriska åtgärder för att skydda dina personuppgifter. IP-adresser lagras tillfälligt och används endast för geografisk identifiering.'
        },
        {
          title: '4. Tredjepartstjänster',
          content: 'Vår webbplats kan använda tredjepartstjänster som:\n• Google Analytics och Google Tag Manager\n• Google Ads för remarketing\n• Cloudinary för bildhosting\n• WhatsApp för kommunikation\n• ipapi.co för IP-baserad geolokalisering\n• Google reCAPTCHA för spamskydd'
        },
        {
          title: '5. Cookies och spårning',
          content: 'Vi använder cookies för att:\n• Komma ihåg dina språkinställningar\n• Analysera webbplatsanvändning\n• Spåra marknadsföringskampanjer (UTM-parametrar)\n• Tillhandahålla personligt innehåll'
        },
        {
          title: '6. Dina rättigheter',
          content: 'Enligt GDPR har du rätt att:\n• Få tillgång till dina personuppgifter\n• Begära korrigering av felaktiga uppgifter\n• Begära radering av dina uppgifter\n• Invända mot behandling\n• Begära dataportabilitet\n• Återkalla samtycke\n\nKontakta oss på sales@villa.ooo.'
        },
        {
          title: '7. Datalagring',
          content: 'Vi lagrar dina uppgifter endast så länge som nödvändigt. Kontaktformulär sparas i upp till 3 år.'
        },
        {
          title: '8. Kontakta oss',
          content: 'Vid frågor, kontakta oss:\n\nE-post: sales@villa.ooo\nWhatsApp: +357 95 989 569\n\nPersonuppgiftsansvarig: Ingemar Ernst d.o.o.'
        }
      ]
    },
    cs: {
      title: 'Zásady ochrany osobních údajů',
      metaDesc: 'Zásady ochrany osobních údajů Villa.ooo - jak shromažďujeme, používáme a chráníme vaše osobní údaje.',
      lastUpdated: 'Poslední aktualizace: duben 2026',
      intro: 'Ve Villa.ooo se zavazujeme chránit vaše soukromí. Tyto Zásady ochrany osobních údajů vysvětlují, jak shromažďujeme, používáme a chráníme vaše osobní údaje při návštěvě našich webových stránek nebo využívání našich služeb.',
      sections: [
        {
          title: '1. Jaké údaje shromažďujeme',
          content: 'Můžeme shromažďovat osobní údaje, které nám dobrovolně poskytnete, když:\n• Odesíláte kontaktní formulář nebo dotaz\n• Žádáte o informace o našich vilách\n• Přihlašujete se k odběru newsletteru\n• Komunikujete s námi e-mailem nebo WhatsAppem\n\nTo může zahrnovat vaše jméno, e-mailovou adresu, telefonní číslo a obsah zprávy.\n\nAutomaticky také shromažďujeme technické informace:\n• IP adresa (pro určení přibližné polohy: země a města)\n• Typ a verze prohlížeče\n• Operační systém\n• Typ zařízení (počítač, mobil, tablet)\n• Rozlišení obrazovky\n• Časové pásmo\n• Odkazující URL\n• UTM parametry (pro sledování marketingových kampaní)'
        },
        {
          title: '2. Jak používáme vaše údaje',
          content: 'Shromážděné údaje používáme k:\n• Odpovídání na vaše dotazy týkající se našich nemovitostí\n• Poskytování informací o vilách odpovídajících vašim preferencím\n• Zasílání aktualizací o nových nabídkách (pokud jste souhlasili)\n• Zlepšování našich webových stránek a služeb\n• Analýze návštěvnosti a chování uživatelů\n• Měření efektivity marketingových kampaní'
        },
        {
          title: '3. Ochrana údajů',
          content: 'Zavádíme vhodná technická a organizační opatření k ochraně vašich osobních údajů. IP adresy jsou ukládány dočasně a používány pouze pro geografickou identifikaci.'
        },
        {
          title: '4. Služby třetích stran',
          content: 'Naše webové stránky mohou využívat služby třetích stran:\n• Google Analytics a Google Tag Manager\n• Google Ads pro remarketing\n• Cloudinary pro hosting obrázků\n• WhatsApp pro komunikaci\n• ipapi.co pro IP geolokaci\n• Google reCAPTCHA pro ochranu před spamem'
        },
        {
          title: '5. Cookies a sledování',
          content: 'Používáme cookies pro:\n• Zapamatování jazykových preferencí\n• Analýzu využití webu\n• Sledování marketingových kampaní (UTM parametry)\n• Personalizaci obsahu'
        },
        {
          title: '6. Vaše práva',
          content: 'Podle GDPR máte právo:\n• Na přístup ke svým osobním údajům\n• Požadovat opravu nepřesných údajů\n• Požadovat vymazání svých údajů\n• Vznést námitku proti zpracování\n• Na přenositelnost údajů\n• Odvolat souhlas\n\nKontaktujte nás na sales@villa.ooo.'
        },
        {
          title: '7. Uchovávání dat',
          content: 'Vaše údaje uchováváme pouze po nezbytnou dobu. Kontaktní formuláře jsou uchovávány až 3 roky.'
        },
        {
          title: '8. Kontakt',
          content: 'V případě dotazů nás kontaktujte:\n\nE-mail: sales@villa.ooo\nWhatsApp: +357 95 989 569\n\nSprávce údajů: Ingemar Ernst d.o.o.'
        }
      ]
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{`${currentContent.title} — Villa.ooo`}</title>
        <meta name="description" content={currentContent.metaDesc} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl()} />
        {/* hreflang for all languages */}
        {hreflangLanguages.map(lang => (
          <link 
            key={lang} 
            rel="alternate" 
            hrefLang={lang} 
            href={lang === 'en' ? 'https://villa.ooo/privacy' : `https://villa.ooo/${lang}/privacy`} 
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://villa.ooo/privacy" />
        <meta property="og:title" content={`${currentContent.title} — Villa.ooo`} />
        <meta property="og:description" content={currentContent.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Villa.ooo" />
        <meta property="og:locale" content={getOgLocale()} />
        <meta property="og:url" content={getCanonicalUrl()} />
      </Helmet>

      {/* Header */}
      <section className="pt-36 pb-8 md:pt-44 md:pb-12 bg-bone" data-testid="privacy-header">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="heading-section font-heading text-night">{currentContent.title}</h1>
            <p className="text-text-secondary mt-2">{currentContent.lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16" data-testid="privacy-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-8"
          >
            <p className="text-text-secondary text-lg leading-relaxed">
              {currentContent.intro}
            </p>

            {currentContent.sections.map((section, index) => (
              <div key={index} className="space-y-3">
                <h2 className="font-heading text-xl text-night">{section.title}</h2>
                <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPage;
